import type { CaseManifestV1 } from "@cold-case/shared/case-manifest";
import { clueDisplayName } from "@cold-case/shared/manifest-display";
import Phaser from "phaser";

import { JOURNAL_CLUES_TAB_INDEX } from "./journal-tab-indices.js";
import { HUD_BOTTOM_RESERVE_PX } from "./hud-layout.js";
import { UI_FONT, UI_TEXT } from "./ui-theme.js";

const Z = 6200;
const TOAST_LIFETIME_MS = 20_000;
const TOAST_MIN_FONT_PX = 14;
const TOAST_FONT_PX = 15;
const TOAST_MAX_W = 560;

let toastBg: Phaser.GameObjects.Rectangle | null = null;
let toastText: Phaser.GameObjects.Text | null = null;
let toastScene: Phaser.Scene | null = null;
let dismissTimer: Phaser.Time.TimerEvent | null = null;

export function clueTitle(manifest: CaseManifestV1, clueId: string): string {
  return clueDisplayName(manifest, clueId);
}

function layoutToast(scene: Phaser.Scene): void {
  if (!toastBg || !toastText || !toastText.visible) return;
  const vw = scene.scale.width;
  const vh = scene.scale.height;
  const maxW = Math.min(TOAST_MAX_W, vw - 24);
  const pad = 14;
  const innerW = maxW - pad * 2;
  const fontPx = Math.max(TOAST_MIN_FONT_PX, TOAST_FONT_PX);
  toastText.setStyle({
    wordWrap: { width: innerW },
    fixedWidth: innerW,
    fontSize: `${fontPx}px`,
    fontFamily: UI_FONT,
    align: "center",
  });
  toastText.setOrigin(0.5, 0.5);
  const th = Math.max(44, toastText.height + pad * 2);
  const cx = vw * 0.5;
  const bottomBarTop = vh - HUD_BOTTOM_RESERVE_PX;
  const cy = bottomBarTop - 12 - th * 0.5;
  toastBg.setPosition(cx, cy).setSize(maxW, th).setOrigin(0.5, 0.5);
  toastText.setPosition(cx, cy);
}

function ensureToastChrome(scene: Phaser.Scene): void {
  if (toastScene !== scene) {
    toastBg?.destroy();
    toastText?.destroy();
    toastBg = null;
    toastText = null;
    toastScene = scene;
  }
  if (toastBg && toastText) return;

  toastBg = scene.add
    .rectangle(0, 0, 200, 60, 0x0a0c12, 0.94)
    .setStrokeStyle(2, 0xc9a227, 1)
    .setScrollFactor(0)
    .setDepth(Z)
    .setVisible(false)
    .setInteractive({ useHandCursor: true });

  toastText = scene.add
    .text(0, 0, "", {
      fontFamily: UI_FONT,
      fontSize: `${TOAST_FONT_PX}px`,
      color: UI_TEXT.primary,
      align: "center",
    })
    .setOrigin(0.5, 0.5)
    .setScrollFactor(0)
    .setDepth(Z + 1)
    .setVisible(false)
    .setInteractive({ useHandCursor: true });

  const dismiss = (): void => {
    toastBg?.setVisible(false);
    toastText?.setVisible(false);
    if (dismissTimer) {
      dismissTimer.remove(false);
      dismissTimer = null;
    }
  };

  const openClues = (): void => {
    dismiss();
    scene.game.events.emit("ccc_open_journal_tab", JOURNAL_CLUES_TAB_INDEX);
  };

  toastBg.on("pointerdown", openClues);
  toastText.on("pointerdown", openClues);
}

export function showDispatchToast(scene: Phaser.Scene, message: string): void {
  ensureToastChrome(scene);
  if (!toastBg || !toastText) return;

  if (dismissTimer) dismissTimer.remove(false);

  toastText.setText(message);
  toastText.setColor(UI_TEXT.primary);
  toastBg.setVisible(true);
  toastText.setVisible(true);
  layoutToast(scene);

  dismissTimer = scene.time.delayedCall(TOAST_LIFETIME_MS, () => {
    toastBg?.setVisible(false);
    toastText?.setVisible(false);
    dismissTimer = null;
  });
}

export function showClueDiscoveryToast(
  scene: Phaser.Scene,
  discovererName: string,
  clueName: string
): void {
  ensureToastChrome(scene);
  if (!toastBg || !toastText) return;

  if (dismissTimer) dismissTimer.remove(false);

  const headline = `${discovererName} has discovered a new clue!`;
  const narrow = scene.scale.width < 480;
  const body = narrow
    ? clueName
    : `${clueName}\nTap to open Clues in your case file.`;
  toastText.setText(`${headline}\n${body}`);
  toastText.setColor(UI_TEXT.primary);
  toastBg.setVisible(true);
  toastText.setVisible(true);
  layoutToast(scene);

  dismissTimer = scene.time.delayedCall(TOAST_LIFETIME_MS, () => {
    toastBg?.setVisible(false);
    toastText?.setVisible(false);
    dismissTimer = null;
  });
}

export function showClueToastForId(
  scene: Phaser.Scene,
  manifest: CaseManifestV1,
  clueId: string,
  discovererName = "You"
): void {
  showClueDiscoveryToast(scene, discovererName, clueDisplayName(manifest, clueId));
}

export function layoutClueToastIfVisible(scene: Phaser.Scene): void {
  layoutToast(scene);
}

/** Hide active clue toast (e.g. when accusation overlays take over). */
export function dismissClueToast(): void {
  if (dismissTimer) {
    dismissTimer.remove(false);
    dismissTimer = null;
  }
  toastBg?.setVisible(false);
  toastText?.setVisible(false);
}
